import { AuthParams } from "@/@types/auth";
import { config } from "@/api/common";
import {
  createContext,
  ReactNode,
  useContext,
  useState,
} from "react";

interface AuthContextValue {
  authenticated: boolean;
  updateAuthentication: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  getSession: () => AuthParams | null;
  setSession: (params: AuthParams) => void;
  getEnvironment: () => string | null;
  setEnvironment: (environment: string) => void;
  getAuthedHeaders: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);

  const clearLocalSession = () => {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("csrf_token");
      localStorage.removeItem("environment");

      // Clear all cookies
      document.cookie.split(';').forEach(cookie => {
          document.cookie = cookie
              .replace(/^ +/, '')
              .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
      });
  }

  const setSession = (params: AuthParams) => {
      localStorage.setItem("access_token", params.access_token);
      localStorage.setItem("refresh_token", params.refresh_token);
      localStorage.setItem("csrf_token", params.csrf_token);
      setAuthenticated(true);
  }

  const getSession = (): AuthParams | null => {
      const access_token = localStorage.getItem("access_token");
      const refresh_token = localStorage.getItem("refresh_token");
      const csrf_token = localStorage.getItem("csrf_token");

      if (access_token && refresh_token && csrf_token) {
          return {
              access_token,
              refresh_token,
              csrf_token,
          };
      }

      return null;
  }

  /** Handled separately from the session as it must be set before */
  const setEnvironment = (environment: string) => {
      localStorage.setItem("environment", environment);
  }

  const getEnvironment = (): string => {
      return localStorage.getItem("environment") || "";
  }

  const updateAuthentication = () => {
      const session = getSession();
      if (!session) {
          setAuthenticated(false);
          return;
      }
      if (session.access_token && session.refresh_token && session.csrf_token) {
          setAuthenticated(true);
      }
  }

  const getAuthedHeaders = () => {
      const session = getSession();
      const environment = getEnvironment();

      return {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}` || "",
          "X-CSRFToken": session?.csrf_token || "",
          "x-refresh-token": session?.refresh_token || "",
          "x-environment": environment,
      };
  }

  const logout = async () => {
    const session = getSession();

    try {
      if (session?.refresh_token) {
        await fetch(config.BASE_URL + "/server-logout/", {
          method: "POST",
          body: JSON.stringify({ refresh: session.refresh_token }),
          headers: getAuthedHeaders(),
        });
      }
    } catch (error) {
      console.error("Error logging out from server:", error);
    } finally {
      clearLocalSession();
      setAuthenticated(false);
    }
  };

  const refresh = async (): Promise<void> => {
    const session = getSession();

    if (!session?.refresh_token) {
      logout();
      return;
    }

    try {
      const response = await fetch(config.BASE_URL + "/token/refresh/", {
        method: "POST",
        body: JSON.stringify({ refresh: session.refresh_token }),
        headers: getAuthedHeaders(),
      });

      if (!response.ok && response.status > 400 && response.status < 500) {
        // Invalid refresh token, logout user
        localStorage.removeItem("refresh_token");
        logout();
        return;
      }

      const json = await response.json();
      localStorage.setItem("access_token", json.new_token.access);
    } catch (error: any) {
      console.error("Error during token refresh:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ authenticated, updateAuthentication, logout, refresh, getSession, setSession, getEnvironment, setEnvironment, getAuthedHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuthContext must be used within an AuthContextProvider"
    );
  }
  return context;
};
